import React, { useState, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import CommentThreads, { ICommentsThreadsComponentProps } from './commentThreads'
import { ICommentThreadObject, CommentReferenceType, IContextMenuState } from 'types'
import { Comment } from 'components/icons'
import Menu from 'components/menu'
import { INITIAL_CONTEXT_MENU_STATE } from 'app-constants'
import { ISpreadsheetData } from 'components/spreadsheet/types'

interface ICommentsComponentProps {
  referenceType: CommentReferenceType
  referenceId: string
  openCommentThreads: number
  permissionLevel: number
  selected: boolean
  spreadsheetData?: ISpreadsheetData
  onSelectedComment: (sectionPulbicId: string) => void
  onCommentThreadsChange: (newCommentThreads: ICommentThreadObject[]) => void
}

const CommentsComponent: React.FC<ICommentsComponentProps> = ({
  referenceType,
  referenceId,
  openCommentThreads,
  permissionLevel,
  selected,
  spreadsheetData,
  onSelectedComment,
  onCommentThreadsChange
}) => {
  const triggerRef = useRef<HTMLDivElement>(null)
  // From the router rather than window.location: a read of window.location while rendering is memoised by the
  // React Compiler, so following a link to a thread on the same page wouldn't update it.
  const [searchParams] = useSearchParams()
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('section') === referenceId && params.has('commentThread')) {
      triggerRef.current?.click()
    }
  }, [referenceId])
  const [currentOpenCommentThreads, setCurrentOpenCommentThreads] = useState(openCommentThreads)
  const [commentMenu, setCommentMenu] = useState<IContextMenuState>(INITIAL_CONTEXT_MENU_STATE)

  const props: ICommentsThreadsComponentProps = {
    referenceType,
    referenceId,
    permissionLevel,
    selected: selected && !searchParams.has('commentThread'),
    spreadsheetData,
    onOpenCommentTreadsChange: (value) => setCurrentOpenCommentThreads(value),
    onCommentThreadsChange: (newCommentThreads: ICommentThreadObject[]) => onCommentThreadsChange(newCommentThreads)
  }

  const commentWidth = 384
  const offset = 0

  return (
    <div className="relative">
      <div
        ref={triggerRef}
        className="text-sm flex items-center justify-center"
        onClick={(event) => {
          onSelectedComment(referenceId)
          // Offsets are relative to the positioned wrapper above, so the popover renders inline and
          // scrolls with the document content instead of being pinned to the viewport.
          const trigger = event.currentTarget
          setCommentMenu({
            open: true,
            top: `${trigger.offsetTop + trigger.offsetHeight + 10}px`,
            left: `${trigger.offsetLeft + trigger.offsetWidth - commentWidth + offset}px`,
            right: 'auto',
            bottom: 'auto'
          })
        }}
      >
        <Comment />
        <span className="ml-0.5">{currentOpenCommentThreads}</span>
      </div>

      {commentMenu.open && (
        <Menu
          id={`comment-thread-${referenceId}`}
          menuState={commentMenu}
          setMenuState={setCommentMenu}
          width={commentWidth}
          inline
        >
          <CommentThreads {...props} />
        </Menu>
      )}
    </div>
  )
}

export default CommentsComponent
